import { SphereDropzone } from "@components/common/sphere-dropzone/sphere-dropzone";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import {
  MultiUploadedFileResponse,
  UploadElementType,
} from "@custom-types/file-upload-types";
import { BaseMarkupProps } from "@custom-types/project-markups-types";
import { neutral, FileDocumentIcon } from "@flat-ui";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { MARKUP_ATTACHMENT_EXTENSIONS_WHITE_LIST } from "@pages/project-details/project-markups/markup-attachment-utils";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { markupAttachmentGroupSelector } from "@store/i-elements/i-elements-selectors";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { cloneDeep, findIndex } from "es-toolkit/compat";

/** Maximum file size of attachment can be uploaded for annotations */
const MAX_ATTACHMENT_SIZE_IN_MB = 1024;

/** Renders dropzone to upload markup attachment  */
export function MarkupUploadAttachment({
  markup,
}: BaseMarkupProps): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const {
    projectId,
    setIsMarkupUpdating,
    markups,
    isAttachmentUploading,
    setIsAttachmentUploading,
    updateMarkups,
    attachments,
    updateAttachments,
  } = useMarkupContext();
  const { addAttachmentsToMarkup } = useProjectMarkupUpdate({ projectId });
  const attachmentGroup = useAppSelector(markupAttachmentGroupSelector(markup));
  const currentUser = useAppSelector(currentUserSelector);
  const { refreshAttachmentIfExpired } = useRefreshAttachment();

  function setIsLoading(isLoading: boolean): void {
    setIsAttachmentUploading(isLoading);
    setIsMarkupUpdating(isLoading);
  }

  /** Triggers when attachment uploading is complete for adding the attachment to the markup */
  async function addAttachment(
    uploadedResponse: MultiUploadedFileResponse
  ): Promise<void> {
    const uploadedFiles = uploadedResponse.successful;

    // Early return if there are no successful uploads because they all failed and/or got canceled
    if (!uploadedFiles.length) {
      return;
    }

    trackEvent({
      name: AnnotationEvents.addNewAttachment,
      props: {
        numberOfFiles: uploadedFiles.length,
        iElementType: markup.type,
        // Adding typeHint if available
        ...(markup.typeHint && { iElementTypeHint: markup.typeHint }),
      },
    });

    setIsLoading(true);

    const mutations = await addAttachmentsToMarkup({
      uploadedFiles,
      markup,
      attachmentGroup,
    });

    if (!mutations || !currentUser) {
      return setIsLoading(false);
    }

    try {
      // Update attachments of the annotation
      const clonedAttachments = cloneDeep(attachments);
      const attachmentIds = mutations.map(
        (mutations) => mutations.newElement.id
      );
      const refreshedAttachments = await refreshAttachmentIfExpired(
        attachmentIds
      );

      const finalUpdatedAttachments = [
        ...clonedAttachments,
        ...refreshedAttachments,
      ];

      updateAttachments(finalUpdatedAttachments);

      // Also update the value of the attachmentsCount property of the annotation, which is used in the annotations table
      const newMarkups = cloneDeep(markups);
      const markupIndex = findIndex(newMarkups, { id: markup.id });
      newMarkups[markupIndex].attachmentsCount = clonedAttachments.length;
      updateMarkups(newMarkups);

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }

  return (
    <SphereDropzone
      titleDragAndDrop={"New Attachment"}
      instruction="Drag & drop here"
      maxFileSize={MAX_ATTACHMENT_SIZE_IN_MB}
      hasUploadFileButton={false}
      shouldShowSupportedFormats={false}
      shouldAllowMultiUpload={true}
      avatar={
        <SphereAvatar
          icon={<FileDocumentIcon sx={{ width: "50px", height: "50px" }} />}
          size="x-large"
          shouldHideWhiteRim
          iconColor={neutral[600]}
          backgroundColor={neutral[100]}
        />
      }
      allowedExtensions={MARKUP_ATTACHMENT_EXTENSIONS_WHITE_LIST}
      isLoading={isAttachmentUploading}
      setIsLoading={setIsAttachmentUploading}
      onUploadComplete={addAttachment}
      context={{
        uploadElementType: UploadElementType.annotation,
        projectId,
        iElementId: markup.id,
      }}
    />
  );
}
